import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Image,
  useColorMode,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getBrandValue } from "../brandConfig";
import { UnifiedLoginModal } from "./authentication/components/UnifiedLoginModal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const SITE_PASSWORD = "123";
const SITE_ACCESS_KEY = "peh_site_access_granted";

const Home = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isAuthenticated } = useAuth();

  // Password protection state
  const [hasAccess, setHasAccess] = useState(false);
  const [password, setPassword] = useState("");
  const [showError, setShowError] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // Refs for GSAP parallax
  const backgroundRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLDivElement>(null);

  // GSAP ScrollTrigger parallax effect
  useEffect(() => {
    let ctx: gsap.Context;

    if (backgroundRef.current && heroSectionRef.current) {
      ctx = gsap.context(() => {
        gsap.set(backgroundRef.current, { yPercent: 0 });

        gsap.to(backgroundRef.current, {
          yPercent: -30,
          ease: "none",
          scrollTrigger: {
            trigger: heroSectionRef.current,
            start: "top top",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
      });

      // Refresh ScrollTrigger after a short delay to ensure proper positioning
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    }

    return () => {
      if (ctx) ctx.revert();
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // Refresh ScrollTrigger when scroll position changes to top
  useEffect(() => {
    const handleScrollToTop = () => {
      const scrollTop = Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      if (scrollTop < 10 && backgroundRef.current) {
        gsap.set(backgroundRef.current, { yPercent: 0 });
        ScrollTrigger.refresh();
      }
    };

    window.addEventListener('scroll', handleScrollToTop, { passive: true });
    document.body.addEventListener('scroll', handleScrollToTop, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollToTop);
      document.body.removeEventListener('scroll', handleScrollToTop);
    };
  }, []);

  // Check if user already has access on mount
  useEffect(() => {
    const accessGranted = localStorage.getItem(SITE_ACCESS_KEY);
    if (accessGranted === "true") {
      setHasAccess(true);
    }
    setIsCheckingAccess(false);
  }, []);

  const handlePasswordSubmit = () => {
    if (password === SITE_PASSWORD) {
      localStorage.setItem(SITE_ACCESS_KEY, "true");
      setHasAccess(true);
      setShowError(false);
    } else {
      setShowError(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handlePasswordSubmit();
    }
  };

  const handleLoginSuccess = () => {
    onClose();
    window.location.reload();
  };

  return (
    <Box
      bg="#FFFFFF"
      minHeight="100vh"
      position="relative"
    >
      {/* Parallax Background Image */}
      <Box
        ref={backgroundRef}
        position="fixed"
        top="0"
        left="0"
        width="100%"
        height="100vh"
        backgroundImage="url('/Photos/Copilot_20260119_121602.png')"
        backgroundSize="cover"
        backgroundPosition="70% center"
        backgroundRepeat="no-repeat"
        zIndex={0}
        willChange="transform"
        _after={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)",
        }}
      />

      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        ref={heroSectionRef}
        position="relative"
        minHeight="85vh"
        width="100%"
        zIndex={1}
      >

        {/* Hero Content */}
        <Box
          position="relative"
          zIndex={2}
          height="100%"
          display="flex"
          alignItems="flex-start"
          pt="22vh"
          pb="5vh"
          pl={{ base: "5vw", md: "10vw", lg: "12vw" }}
          pr="5vw"
        >
          <VStack
            align="flex-start"
            spacing="1.5vw"
            maxW={{ base: "90%", md: "45vw" }}
          >
            <Heading
              as="h1"
              fontSize="clamp(1.3rem, 3vw, 2.8rem)"
              fontWeight="700"
              color="#FFFFFF"
              lineHeight="1.1"
              fontFamily={getBrandValue("fonts.heading")}
              textShadow="0 2px 10px rgba(0,0,0,0.3)"
            >
              Flourish together
            </Heading>

            <Text
              fontSize="clamp(0.8rem, 1.4vw, 1.2rem)"
              fontWeight="600"
              color="#FFFFFF"
              lineHeight="1.5"
              fontFamily={getBrandValue("fonts.body")}
              textShadow="0 1px 5px rgba(0,0,0,0.3)"
            >
              Vanguard Care delivers innovative residential<br />aged care in Queensland.
            </Text>

            <Box
              as="button"
              position="relative"
              display="flex"
              alignItems="center"
              bg="transparent"
              pl={0}
              pr="1vw"
              py={2}
              h="clamp(40px, 3.5vw, 48px)"
              onClick={() => navigate("/about-us")}
              transition="all 0.3s ease"
              cursor="pointer"
              border="none"
              role="group"
              _hover={{
                transform: "translateY(-2px)",
              }}
            >
              <Box
                position="absolute"
                left="0"
                top="50%"
                transform="translateY(-50%)"
                w="clamp(30px, 2.5vw, 36px)"
                h="clamp(30px, 2.5vw, 36px)"
                borderRadius="full"
                bg="#FF6600"
                transition="all 0.4s ease"
                zIndex={0}
                _groupHover={{
                  w: "100%",
                  borderRadius: "full",
                }}
              />
              <Text
                color="#FFFFFF"
                fontSize="clamp(0.9rem, 1.3vw, 1.1rem)"
                fontWeight="700"
                fontFamily={getBrandValue("fonts.body")}
                position="relative"
                zIndex={1}
                pl="clamp(10px, 1vw, 12px)"
              >
                Find out more
              </Text>
            </Box>
          </VStack>
        </Box>

        {/* Curved Blue Section Overlay - Arc Opening Upward (smile shape) */}
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          height="25vw"
          minHeight="180px"
          maxHeight="380px"
          overflow="visible"
        >
          <svg
            viewBox="0 0 1440 380"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%", display: "block" }}
            preserveAspectRatio="none"
          >
            <path
              d="M0 0 Q720 670 1440 0 L1440 380 L0 380 Z"
              fill="#03177A"
            />
          </svg>
        </Box>
      </Box>

      {/* Blue Section with Mission Statement */}
      <Box
        bg="#03177A"
        position="relative"
        pt={{ base: 0, md: 0 }}
        pb={{ base: 24, md: 32 }}
        px={{ base: 6, md: 12 }}
        overflow="hidden"
        zIndex={2}
      >

        <Container maxW="1000px" position="relative" zIndex={1}>
          <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              fontWeight="600"
              color="#FFFFFF"
              lineHeight="1.8"
              fontFamily={getBrandValue("fonts.body")}
            >
              We work collaboratively with residents, their families, and our dedicated team to create supportive and welcoming environments where seniors can enjoy a fulfilling and independent lifestyle.
            </Text>

            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              fontWeight="600"
              color="#FFFFFF"
              lineHeight="1.8"
              fontFamily={getBrandValue("fonts.body")}
            >
              Through a personalised, community-focused approach and the use of the latest technology, we provide seamless, reliable, and compassionate care in a comfortable and homely setting, ensuring residents can{" "}
              <Text as="span" color="#FF6600">
                truly flourish.
              </Text>
            </Text>

                      </VStack>
        </Container>
      </Box>

      {/* Additional Content Section - White Background */}
      <Box
        bg="#FFFFFF"
        py={{ base: 16, md: 24 }}
        px={{ base: 6, md: 12 }}
        position="relative"
        zIndex={2}
      >
        <Container maxW="1200px">
          <VStack spacing={{ base: 8, md: 12 }} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="600"
              color="#03177A"
              fontFamily={getBrandValue("fonts.heading")}
            >
              Why Choose Vanguard Care?
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              fontWeight="400"
              color="#444444"
              lineHeight="1.8"
              maxW="800px"
              fontFamily={getBrandValue("fonts.body")}
            >
              At Vanguard Care, we believe in delivering exceptional aged care services that prioritize dignity, respect, and quality of life for every resident.
            </Text>

            <Button
              size="lg"
              bg="#03177A"
              color="#FFFFFF"
              borderRadius="full"
              px={10}
              py={6}
              fontSize="md"
              fontWeight="500"
              fontFamily={getBrandValue("fonts.body")}
              _hover={{
                bg: "#030C61",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(3, 23, 122, 0.3)"
              }}
              onClick={() => navigate("/corporate-services")}
              transition="all 0.3s ease"
            >
              Our Services
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />

      {/* Login Modal */}
      <UnifiedLoginModal
        isOpen={isOpen}
        onClose={onClose}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Password Protection Modal */}
      <Modal
        isOpen={!hasAccess && !isCheckingAccess}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
        size="md"
      >
        <ModalOverlay bg="rgba(3, 23, 122, 0.3)" backdropFilter="blur(10px)" />
        <ModalContent
          bg="#FFFFFF"
          border="1px solid rgba(3, 23, 122, 0.1)"
          borderRadius="lg"
          mx={4}
          boxShadow="0 20px 60px rgba(3, 23, 122, 0.15)"
        >
          <ModalHeader textAlign="center" pt={8} pb={2}>
            <Image
              src="/logo/VC_Main Logo_RGB.png"
              alt="Vanguard Care Logo"
              height="100px"
              objectFit="contain"
              mx="auto"
              mb={4}
            />
            <Text
              fontSize="xl"
              fontWeight="600"
              color="#03177A"
              fontFamily={getBrandValue("fonts.heading")}
            >
              We're Getting Ready
            </Text>
          </ModalHeader>
          <ModalBody pb={8}>
            <VStack spacing={6}>
              <Text
                fontSize="md"
                color="#030C61"
                textAlign="center"
                fontFamily={getBrandValue("fonts.body")}
              >
                Our website is currently being prepared for launch. If you have been provided with an access code, please enter it below.
              </Text>

              <VStack spacing={3} width="100%">
                <Input
                  placeholder="Enter access code"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setShowError(false);
                  }}
                  onKeyPress={handleKeyPress}
                  bg="#E6EEFF"
                  border="1px solid rgba(3, 23, 122, 0.2)"
                  color="#03177A"
                  _placeholder={{ color: "rgba(3, 23, 122, 0.4)" }}
                  _hover={{ borderColor: "rgba(255, 102, 0, 0.3)" }}
                  _focus={{
                    borderColor: "#FF6600",
                    boxShadow: "0 0 0 1px #FF6600"
                  }}
                  textAlign="center"
                  fontSize="lg"
                  py={6}
                />

                {showError && (
                  <Text
                    fontSize="sm"
                    color="#FF6600"
                    textAlign="center"
                  >
                    Incorrect access code. Please try again.
                  </Text>
                )}

                <Button
                  width="100%"
                  bg="#FF6600"
                  color="#FFFFFF"
                  _hover={{
                    bg: "#CC4902",
                    transform: "translateY(-1px)"
                  }}
                  onClick={handlePasswordSubmit}
                  py={6}
                  fontSize="md"
                  fontFamily={getBrandValue("fonts.body")}
                  fontWeight="500"
                  letterSpacing="1px"
                  textTransform="uppercase"
                >
                  Enter Site
                </Button>
              </VStack>

              <Box
                width="100%"
                height="1px"
                bg="rgba(3, 23, 122, 0.1)"
                my={2}
              />

              <Text
                fontSize="sm"
                color="#030C61"
                textAlign="center"
                fontFamily={getBrandValue("fonts.body")}
              >
                Please check back soon. We appreciate your patience.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Home;
